export interface ReciprocalChargeInfo {
  homeClub: string;
  memberName: string;
  memberNumber: string;
}

export const RECIPROCAL_TENDER_LABEL = 'RECIPROCAL';

const text = (value: unknown): string => {
  if (typeof value !== 'string') {
    return '';
  }
  return value.trim();
};

const populatedVenueName = (venue: unknown): string => {
  if (!venue || typeof venue !== 'object') {
    return '';
  }
  return text((venue as { name?: string }).name);
};

/** Identity printed on a reciprocal receipt and shown on the transaction row. */
export const reciprocalBlockFromTransaction = (
  transaction: any
): ReciprocalChargeInfo | undefined => {
  if (!transaction?.isReciprocal) {
    return undefined;
  }
  return {
    homeClub:
      text(transaction.reciprocalVenueName) || populatedVenueName(transaction.reciprocalVenueId),
    memberName: text(transaction.reciprocalMemberName),
    memberNumber: text(transaction.reciprocalMemberNumber),
  };
};

const BARE_GUEST_NAMES = new Set(['unknown', 'unknown guest']);

const isBareGuestName = (name: string): boolean => BARE_GUEST_NAMES.has(name.toLowerCase());

/** Order documents and Order model instances both carry guest + transactions. */
const unwrapOrder = (order: any): any => {
  const data = order?.data;
  if (data && typeof data === 'object' && ('transactions' in data || 'guest' in data)) {
    return data;
  }
  return order;
};

const guestNameOf = (order: any): string => {
  const guest = order?.guest;
  if (!guest || typeof guest !== 'object') {
    return '';
  }
  return text(guest.name);
};

/** Sale preferred, so a later refund does not hide the original charge. */
export const reciprocalTransactionOnOrder = (order: any): any | undefined => {
  const transactions = unwrapOrder(order)?.transactions;
  if (!Array.isArray(transactions)) {
    return undefined;
  }
  const reciprocal = transactions.filter(
    (transaction: any) => transaction && typeof transaction === 'object' && transaction.isReciprocal
  );
  return reciprocal.find((transaction: any) => transaction.type !== 'refund') || reciprocal[0];
};

/**
 * "River Oaks — Jane Smith 1234" for a transaction row.
 * When the charge did not store a member name, the guest name is used if it is a real name.
 */
export const reciprocalMethodSuffix = (transaction: any, guestName?: string | null): string => {
  const block = reciprocalBlockFromTransaction(transaction);
  if (!block) {
    return '';
  }
  const guest = text(guestName);
  const memberName = block.memberName || (guest && !isBareGuestName(guest) ? guest : '');
  const identity = [memberName, block.memberNumber].filter(Boolean).join(' ');
  return [block.homeClub, identity].filter(Boolean).join(' — ');
};

/**
 * Order-row name. A reciprocal charge shows home club, member name, and member number
 * instead of a bare guest name ("Casey Visitor" or "Unknown"). A different guest name
 * stays in front of that identity.
 */
export const orderGuestLabel = (order: any): string => {
  const data = unwrapOrder(order);
  const guestName = guestNameOf(data);
  const transaction = reciprocalTransactionOnOrder(data);
  const block = reciprocalBlockFromTransaction(transaction);
  if (!block) {
    return guestName;
  }
  const memberName =
    block.memberName || (guestName && !isBareGuestName(guestName) ? guestName : '');
  const identity = [memberName, block.memberNumber].filter(Boolean).join(' ');
  const label = [block.homeClub, identity].filter(Boolean).join(' — ');
  if (!label) {
    return guestName;
  }
  const guestIsMember = !!block.memberName && guestName === block.memberName;
  const guestUsedAsMember = !block.memberName && memberName === guestName && !!guestName;
  if (!guestName || isBareGuestName(guestName) || guestIsMember || guestUsedAsMember) {
    return label;
  }
  return `${guestName} — ${label}`;
};

/**
 * Labels under the RECIPROCAL tender line and again in the signature block.
 * Order matches the expo receipt: HOME CLUB, MEMBER NAME, MEMBER #.
 */
export const reciprocalReceiptFields = (
  reciprocal: ReciprocalChargeInfo
): { label: string; value: string }[] => [
  { label: 'HOME CLUB', value: reciprocal.homeClub },
  { label: 'MEMBER NAME', value: reciprocal.memberName },
  { label: 'MEMBER #', value: reciprocal.memberNumber },
];

/** Walk-up reciprocal orders are named Unknown. Omit that name instead of printing it. */
export const printableCustomerName = (name?: string | null): string => {
  const trimmed = text(name);
  if (!trimmed || trimmed === 'Unknown') {
    return '';
  }
  return trimmed;
};
